import { createPersistedStore } from 'hooks/state/usePersistedStore';
import { createReadMessagesSlice, ReadMessagesSlice } from 'hooks/state/ReadMessagesSlice';
import { useContext, useMemo, useCallback } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useAuth0 } from '@auth0/auth0-react';
import { differenceInDays, parseISO } from 'date-fns';
import { TradingMessage } from 'pages/trading/model';

export {
  type ReadMessagesSlice,
  type ReadMessagesState,
  type ReadMessagesActions,
} from 'hooks/state/ReadMessagesSlice';

export const useReadMessagesStore = createPersistedStore<ReadMessagesSlice>(
  createReadMessagesSlice,
  'trading-read-messages',
  () => localStorage
);

export function useUnreadMessageCount(totalMessageCount: number): number {
  const { accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const readMessageIds = useReadMessagesStore(state => state.readMessageIdsByUserScope);

  const accountKey = accountNumber?.trim() || 'default';
  const userKey = user?.sub?.trim() || 'anonymous';
  const scopeKey = `${accountKey}:${userKey}`;
  const readIds = readMessageIds[scopeKey] ?? [];
  return Math.max(0, totalMessageCount - readIds.length);
}

export function useUnreadMessageIds(allMessageIds: number[]): number[] {
  const { accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const readMessageIds = useReadMessagesStore(state => state.readMessageIdsByUserScope);

  const accountKey = accountNumber?.trim() || 'default';
  const userKey = user?.sub?.trim() || 'anonymous';
  const scopeKey = `${accountKey}:${userKey}`;
  const readIds = readMessageIds[scopeKey] ?? [];
  return allMessageIds.filter(id => !readIds.includes(id));
}

export function filterMessagesByDays(
  messages: TradingMessage[],
  days: number,
  isSuperAdmin: boolean
): TradingMessage[] {
  if (isSuperAdmin) {
    return messages;
  }

  const today = new Date();
  return messages.filter(message => {
    const messageDate = parseISO(message.currentDataVersion);
    const daysAgo = differenceInDays(today, messageDate);
    return daysAgo <= days;
  });
}

export function useUnreadMessageCountFiltered(messages: TradingMessage[] | undefined, isSuperAdmin: boolean): number {
  const { accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const readMessageIds = useReadMessagesStore(state => state.readMessageIdsByUserScope);

  return useMemo(() => {
    if (!messages) return 0;

    // Filter messages to last 7 days
    const filteredMessages = filterMessagesByDays(messages, 7, isSuperAdmin);

    // Get read message IDs for this user scope
    const accountKey = accountNumber?.trim() || 'default';
    const userKey = user?.sub?.trim() || 'anonymous';
    const scopeKey = `${accountKey}:${userKey}`;
    const readIds = readMessageIds[scopeKey] ?? [];

    // Count unread messages from filtered set
    const unreadMessages = filteredMessages.filter(msg => !readIds.includes(msg.messageId));
    return unreadMessages.length;
  }, [messages, isSuperAdmin, readMessageIds, accountNumber, user?.sub]);
}

export function useMarkSteeringHubVisited(): () => void {
  const { accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const markSteeringHubVisited = useReadMessagesStore(state => state.markSteeringHubVisited);

  return useCallback(() => {
    markSteeringHubVisited(accountNumber, user?.sub);
  }, [accountNumber, user?.sub, markSteeringHubVisited]);
}

export function useHasVisitedSteeringHub(): boolean {
  const { accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const steeringHubVisitedByUserScope = useReadMessagesStore(state => state.steeringHubVisitedByUserScope);

  const accountKey = accountNumber?.trim() || 'default';
  const userKey = user?.sub?.trim() || 'anonymous';
  const scopeKey = `${accountKey}:${userKey}`;
  return steeringHubVisitedByUserScope[scopeKey] ?? false;
}
